import { ExtensionFragment, ProfileFragment } from '@/services/graphql';
import { getPriorityStatus } from '@/services/priority-management';
import { STANDALONE_NOTE_CATEGORY } from '@bariendo/lib/codings';
import { LocationFragment } from '@/services/graphql';
import {
  getExternalNumberFromPhoneCommunication,
  getInternalNumberFromPhoneCommunication,
  getPayloadByType,
  getCommunicationDirection,
  getFaxCounterpartyLabel,
  hasCategory,
  isTimelinePatientFaxCommunication,
} from '@bariendo/lib/communication';
import { isVoiceRecordingPayloadId, selectRecordingAttachmentsForTimeline } from '@bariendo/lib/call-recording';
import { isProviderRoutingHistoryEntry } from '@bariendo/lib/call-routing-history';
import { isCiIntroCallDraft, isCiIntroCallNote, parseCiIntroCallAnswers } from '@bariendo/lib/patient-access-note';
import {
  isWarmHandoffDraft,
  isWarmHandoffNote,
  parseWarmHandoffAnswers,
} from '@bariendo/lib/patient-access-warm-handoff-note';
import {
  CommunicationCategory,
  CommunicationDirection,
  CommunicationMedium,
  DOCUMENT_ATTACHMENT_PAYLOAD_ID,
  RICH_TEXT_CONTENT_PAYLOAD_ID,
  TEXT_CONTENT_PAYLOAD_ID,
  VIRTUAL_RECEPTIONIST_PAYLOAD_ID,
} from '@bariendo/lib/constants';
import {
  EDITED_COMMUNICATION_PAYLOAD_EXTENSION,
  INCOMING_CALL_ROUTING_HISTORY_EXTENSION,
  SMS_ERROR_DETAILS_EXTENSION,
  TWILIO_CALL_ERROR_EXTENSION,
} from '@bariendo/lib/extensions';
import { getConfidentialityLevel } from '@bariendo/lib/permissions';
import { normalizePhoneNumber } from '@bariendo/lib/phone-validations';
import {
  createReference,
  formatCodeableConcept,
  formatReferenceString,
  getExtension,
  getReferenceString,
  isEmpty,
} from '@medplum/core';
import {
  Attachment,
  Communication,
  Encounter,
  MedicationRequest,
  Reference,
  Resource,
  ServiceRequest,
  Task as FhirTask,
} from '@medplum/fhirtypes';
import { getClinicalTaskCompletedBy } from '@bariendo/lib/clinical-tasks';
import { Buffer } from 'buffer';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
  DocumentReferenceFragment,
  TimelineCommunicationResource,
  TimelineEncounterResource,
  AppointmentFragment,
  TimelineEligibilityFormResource,
} from '@/pages/PatientTimelinePage/graphql';
import {
  isChatCommunication,
  isCiIntroCall,
  isWarmHandoff,
  isSmsCommunication,
  isStandaloneNote,
  isVoiceCommunication,
  isFaxTimelineCommunication,
  TimelineChatCommunication,
  TimelineEncounter,
  TimelineGroup,
  TimelineItemType,
  TimelineLabOrder,
  TimelineLabOrderResults,
  TimelineNote,
  TimelinePatientCommunication,
  TimelinePrescriptionOrder,
  TimelineSmsCommunication,
  TimelineStandaloneNoteCommunication,
  TimelineVoiceCommunication,
  TimelineCiIntroCallCommunication,
  TimelineWarmHandoffCommunication,
  TimelineFaxCommunication,
  TimelineEligibilityForm,
  TimelinePaymentOrder,
  TimelineTaskCompleted,
  TimelinePaymentEvent,
  TimelineBenefitEvent,
  TimelineRefundEvent,
  TwilioCallError,
  VirtualReceptionistData,
  TimelineAppointment,
} from '@/components/Timeline/types';
import { clearBlankExtensionValues } from '@bariendo/lib/services';
import {
  deriveVoiceCallOutcome,
  getVoiceCallRecordingUnavailableMessage,
  isMeaningfulTwilioCallError,
} from '@bariendo/lib/voice-call-outcome';
import { shouldIncludeHealthieLabOrderInTimeline } from '@/services/Timeline/healthieLabOrderTimelineFilter';
import {
  extractVirtualServiceUrlFromAppointment,
  extractVirtualServicePhoneNumberFromAppointment,
  extractVirtualServiceAdminUrlFromAppointment,
  extractLocationFromAppointment,
  extractContactTypeFromAppointment,
} from '@bariendo/lib/scheduling';

dayjs.extend(relativeTime);

/**
 * Extracts virtual receptionist data from communication payload
 * @param payload - Array of communication payload items
 * @param communication - The communication resource to check categories
 * @returns Virtual receptionist data if available
 */
function extractVirtualReceptionistData(
  payload: TimelineCommunicationResource['payload'],
  communication: TimelineCommunicationResource
): VirtualReceptionistData | null {
  // Check if the call was forwarded to virtual receptionist
  const isForwarded = communication.category?.some((cat) =>
    cat.coding?.some((coding) => coding.code === CommunicationCategory.VIRTUAL_RECEPTIONIST_FORWARD)
  );

  if (!isForwarded) {
    return null;
  }

  const virtualReceptionistPayload = payload?.find((payload) => payload.id === VIRTUAL_RECEPTIONIST_PAYLOAD_ID);

  if (!virtualReceptionistPayload?.contentAttachment?.data) {
    return null;
  }

  try {
    const decodedData = Buffer.from(virtualReceptionistPayload.contentAttachment.data, 'base64').toString();
    return JSON.parse(decodedData) as VirtualReceptionistData;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to parse virtual receptionist data:', error);
    return null;
  }
}

/**
 * Extracts Routing History Extension data from communication payload
 * @param communication - The communication resource to check categories
 * @returns Incoming Routing History data if available
 */
function extractVirtualReceptionistProvidersTriedHistory(
  communication: TimelineCommunicationResource
): ExtensionFragment[] {
  const routingExtensionHistory = communication.extension?.find(
    (extension) => extension.url === INCOMING_CALL_ROUTING_HISTORY_EXTENSION
  )?.extension;
  // The container also carries skip entries (BAR-2621) — a reason, no practitioner. This
  // list is rendered as the providers a call was tried on, so only the provider entries
  // belong in it.
  return (routingExtensionHistory ?? []).filter((extension) => isProviderRoutingHistoryEntry(extension));
}

/**
 * Extracts Twilio error data from communication extension
 * @param communication - The communication resource to check for errors
 * @returns Twilio error data if available
 */
function extractTwilioCallError(communication: TimelineCommunicationResource): TwilioCallError[] | undefined {
  const errorExtension = getExtension(communication, TWILIO_CALL_ERROR_EXTENSION);
  if (!errorExtension?.extension || errorExtension.extension.length === 0) {
    return undefined;
  }

  return errorExtension.extension
    .map((errorInstance) => {
      const rawData = errorInstance.valueAttachment?.data;
      if (!rawData) {
        return {} as TwilioCallError;
      }
      try {
        const decodedData = Buffer.from(rawData, 'base64').toString('utf-8');
        return JSON.parse(decodedData) as TwilioCallError;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to parse Twilio call error attachment:', error);
        return {} as TwilioCallError;
      }
    })
    .filter((error) => !isEmpty(error) && isMeaningfulTwilioCallError(error));
}

/**
 * Document-style parts: SMS/MMS `media-attachment*` + DocumentReference, plus inline PDF on payload.contentAttachment (eFax).
 * Voice-call-only artifacts (recording, Twilio errors, virtual receptionist) are not computed here.
 */
function getSmsStyleAndInlinePdfAttachments(payload: TimelineCommunicationResource['payload']): {
  mediaAttachments: Attachment[];
  pdfAttachments: Attachment[];
} {
  const smsAttachments = payload?.filter((p) => !!p.id?.startsWith(DOCUMENT_ATTACHMENT_PAYLOAD_ID));

  const mediaAttachments = (smsAttachments
    ?.filter((p) =>
      (p.contentReference?.resource as DocumentReferenceFragment)?.content?.[0]?.attachment?.contentType?.startsWith(
        'image/'
      )
    )
    ?.map((p) => (p.contentReference?.resource as DocumentReferenceFragment)?.content?.[0]?.attachment) ??
    []) as Attachment[];

  const pdfAttachmentsFromDocumentReference = smsAttachments
    ?.filter(
      (p) =>
        (p.contentReference?.resource as DocumentReferenceFragment)?.content?.[0]?.attachment?.contentType ===
        'application/pdf'
    )
    ?.map((p) => (p.contentReference?.resource as DocumentReferenceFragment)?.content?.[0]?.attachment) as Attachment[];

  /** Medplum eFax and similar store the PDF on payload.contentAttachment, not DocumentReference + media-attachment id. */
  const pdfAttachmentsFromInlinePayload = (payload ?? [])
    .filter(
      (p) =>
        !isVoiceRecordingPayloadId(p.id) && Boolean(p.contentAttachment?.contentType?.startsWith('application/pdf'))
    )
    .map((p) => p.contentAttachment as Attachment);

  const pdfAttachments = [...(pdfAttachmentsFromDocumentReference ?? []), ...pdfAttachmentsFromInlinePayload];

  return { mediaAttachments, pdfAttachments };
}

/**
 * Builds payload data for SMS/MMS communications and inline PDF parts (e.g. inbound eFax).
 * @param payload - Array of communication payload items
 * @param communication - The communication resource to check categories
 * @returns Object containing attachments and virtual receptionist data
 */
function getPayloadAttachments(
  payload: TimelineCommunicationResource['payload'],
  communication: TimelineCommunicationResource
) {
  const recordingAttachments = selectRecordingAttachmentsForTimeline(payload);

  const { mediaAttachments, pdfAttachments } = getSmsStyleAndInlinePdfAttachments(payload);

  const virtualReceptionistData = extractVirtualReceptionistData(payload, communication);
  const virtualReceptionistProvidersTriedHistory = extractVirtualReceptionistProvidersTriedHistory(communication);
  const twilioErrors = extractTwilioCallError(communication);

  return {
    recordingAttachments,
    mediaAttachments,
    pdfAttachments,
    virtualReceptionistData,
    virtualReceptionistProvidersTriedHistory,
    twilioErrors,
  };
}

function calculateCommunicationDuration(communication: TimelineCommunicationResource): number | undefined {
  if (!communication.sent || !communication.received) return undefined;

  // Calculate duration for phone calls
  const sentTime = new Date(communication.sent).getTime();
  const receivedTime = new Date(communication.received).getTime();
  const durationMs = receivedTime - sentTime;
  return durationMs > 0 ? Math.floor(durationMs / 1000) : undefined;
}

function mapResourceToPatientTimelineCommunication(
  communication: TimelineCommunicationResource
): TimelinePatientCommunication | null {
  if (!communication.id) return null;
  const primaryInstant = communication.sent ?? communication.received;
  if (!primaryInstant) return null;

  const isLabOrder = communication.category?.some((cat) =>
    cat.coding?.some((coding) => coding.code === CommunicationCategory.LAB_ORDER)
  );

  const medium = communication.medium?.[0]?.coding?.[0]?.code;
  const isPhoneCallMedium = medium === CommunicationMedium.PHONE_CALL;
  const isSmsMedium = ([CommunicationMedium.SMS, CommunicationMedium.MMS] as string[]).includes(medium!);
  const isChatMedium = medium === CommunicationMedium.CHAT;
  const isStandaloneNote = communication.category?.some((cat) =>
    cat.coding?.some((coding) => coding.code === STANDALONE_NOTE_CATEGORY.code)
  );

  const shouldMapCommunicationAsFax = isTimelinePatientFaxCommunication(communication as Communication);

  /**
   * Checked before the medium branches below: a PAC intro-call note sits in the
   * Conversations group and would otherwise be mistaken for whatever medium it
   * carries.
   */
  if (isCiIntroCallNote(communication as Communication)) {
    return {
      reference: createReference(communication as Communication),
      resource: communication,
      timestamp: new Date(primaryInstant),
      status: communication.status,
      sender: communication.sender?.resource,
      recipient: communication.recipient?.[0]?.resource,
      content: getPayloadByType(communication, TEXT_CONTENT_PAYLOAD_ID)?.contentString,
      direction: undefined,
      confidentiality: getConfidentialityLevel(communication),
      hiddenPayload: communication.payload === null,
      priorityStatus: 'normal',
      isAssignable: false,
      type: TimelineItemType.PAC_INTRO_CALL,
      answers: parseCiIntroCallAnswers(communication as Communication),
      isDraft: isCiIntroCallDraft(communication as Communication),
    } as TimelineCiIntroCallCommunication;
  }

  /**
   * Same reason as the intro-call branch above, and likewise ahead of the medium
   * checks: the warm hand-off note belongs in Conversations, not to whatever
   * medium it happens to carry.
   */
  if (isWarmHandoffNote(communication as Communication)) {
    return {
      reference: createReference(communication as Communication),
      resource: communication,
      timestamp: new Date(primaryInstant),
      status: communication.status,
      sender: communication.sender?.resource,
      recipient: communication.recipient?.[0]?.resource,
      content: getPayloadByType(communication, TEXT_CONTENT_PAYLOAD_ID)?.contentString,
      direction: undefined,
      confidentiality: getConfidentialityLevel(communication),
      hiddenPayload: communication.payload === null,
      priorityStatus: 'normal',
      isAssignable: false,
      type: TimelineItemType.PAC_WARM_HANDOFF,
      answers: parseWarmHandoffAnswers(communication as Communication),
      isDraft: isWarmHandoffDraft(communication as Communication),
    } as TimelineWarmHandoffCommunication;
  }

  // Determine direction from category field instead of sender/recipient
  const direction = communication.category?.find((cat) =>
    Object.values(CommunicationDirection).includes(cat.coding?.[0]?.code as CommunicationDirection)
  )?.coding?.[0]?.code as CommunicationDirection | undefined;

  if (shouldMapCommunicationAsFax) {
    const content = getPayloadByType(communication, TEXT_CONTENT_PAYLOAD_ID)?.contentString;
    const { mediaAttachments, pdfAttachments } = getSmsStyleAndInlinePdfAttachments(communication.payload ?? []);
    const baseItem = {
      reference: createReference(communication as Communication),
      resource: communication,
      timestamp: new Date(primaryInstant),
      status: communication.status,
      sender: communication.sender?.resource,
      recipient: communication.recipient?.[0]?.resource,
      content,
      direction,
      confidentiality: getConfidentialityLevel(communication),
      hiddenPayload: communication.payload === null,
      priorityStatus: 'normal',
      isAssignable: false,
    };
    const asCommunication = communication as Communication;
    const faxDirection = getCommunicationDirection(asCommunication);
    const faxCounterparty = getFaxCounterpartyLabel(asCommunication);

    return {
      ...baseItem,
      type: TimelineItemType.FAX,
      direction: faxDirection,
      mediaAttachments,
      pdfAttachments,
      faxCounterparty,
    } as TimelineFaxCommunication;
  }

  const patientNumber = normalizePhoneNumber(getExternalNumberFromPhoneCommunication(communication) ?? '');
  // Our end of the same call/text. Both numbers are extensions on the resource
  // already in hand, so naming the Everself line costs no additional read.
  const orbitNumber = normalizePhoneNumber(getInternalNumberFromPhoneCommunication(communication) ?? '');

  const content = getPayloadByType(communication, TEXT_CONTENT_PAYLOAD_ID)?.contentString;

  const {
    recordingAttachments,
    mediaAttachments,
    pdfAttachments,
    virtualReceptionistData,
    virtualReceptionistProvidersTriedHistory,
    twilioErrors,
  } = getPayloadAttachments(communication.payload ?? [], communication);

  const baseItem = {
    reference: createReference(communication as Communication),
    resource: communication,
    timestamp: new Date(primaryInstant),
    status: communication.status,
    sender: communication.sender?.resource,
    recipient: communication.recipient?.[0]?.resource,
    content,
    direction,
    confidentiality: getConfidentialityLevel(communication),
    hiddenPayload: communication.payload === null,
    priorityStatus: 'normal',
    isAssignable: false,
  };

  const assigneeTask = communication.assigneeTask?.[0];

  if (isPhoneCallMedium) {
    const callOutcome = deriveVoiceCallOutcome(communication as Communication);
    const recordingUnavailableMessage = getVoiceCallRecordingUnavailableMessage(
      callOutcome,
      communication as Communication
    );

    return {
      ...baseItem,
      type: TimelineItemType.VOICE,
      duration: calculateCommunicationDuration(communication),
      recordingAttachments,
      patientNumber,
      orbitNumber,
      content: JSON.stringify(virtualReceptionistData),
      virtualReceptionistData,
      virtualReceptionistProvidersTriedHistory,
      twilioErrors,
      callOutcome,
      recordingUnavailableMessage,
      priorityStatus: getPriorityStatus(assigneeTask?.priority, assigneeTask?.executionPeriod),
      isAssignable:
        hasCategory(communication, CommunicationCategory.VIRTUAL_RECEPTIONIST_FORWARD) &&
        direction === CommunicationDirection.INCOMING,
    } as TimelineVoiceCommunication;
  } else if (isSmsMedium) {
    // Extract all error details from extensions if present
    const errorExtensions = communication.extension?.filter((ext) => ext.url === SMS_ERROR_DETAILS_EXTENSION) || [];
    const smsErrors = errorExtensions
      .map((ext) => {
        try {
          return JSON.parse(ext.valueString || '{}');
        } catch {
          return {};
        }
      })
      .filter((error) => error.errorCode || error.errorMessage);

    const latestError = smsErrors.length > 0 ? smsErrors[smsErrors.length - 1] : {};

    return {
      ...baseItem,
      type: TimelineItemType.SMS,
      mediaAttachments,
      pdfAttachments,
      patientNumber,
      orbitNumber,
      errorCode: latestError.errorCode,
      errorMessage: latestError.errorMessage,
      errors: smsErrors,
      priorityStatus: getPriorityStatus(assigneeTask?.priority, assigneeTask?.executionPeriod),
      isAssignable: direction === CommunicationDirection.INCOMING,
    } as TimelineSmsCommunication;
  } else if (isChatMedium) {
    const richTextPayload = getPayloadByType(communication, RICH_TEXT_CONTENT_PAYLOAD_ID)?.contentString;
    const richTextContent = richTextPayload
      ? (() => {
          try {
            return JSON.parse(richTextPayload);
          } catch {
            return undefined;
          }
        })()
      : undefined;

    return {
      ...baseItem,
      type: TimelineItemType.CHAT,
      mediaAttachments,
      pdfAttachments,
      thread: communication.partOf?.[0] as Reference<Communication>,
      richTextContent,
      priorityStatus: getPriorityStatus(assigneeTask?.priority, assigneeTask?.executionPeriod),
      isAssignable: direction === CommunicationDirection.INCOMING,
    } as TimelineChatCommunication;
  } else if (isStandaloneNote) {
    const richTextContent = getPayloadByType(communication, RICH_TEXT_CONTENT_PAYLOAD_ID)?.contentString;
    return {
      ...baseItem,
      type: TimelineItemType.STANDALONE_NOTE,
      richTextContent: richTextContent ? JSON.parse(richTextContent) : undefined,
      isEdited: communication.payload?.some((payload) =>
        payload.extension?.some((ext) => ext.url === EDITED_COMMUNICATION_PAYLOAD_EXTENSION && ext.valueDateTime)
      ),
    } as TimelineStandaloneNoteCommunication;
  } else if (isLabOrder) {
    // Extract ServiceRequest references from basedOn (exclude QuestionnaireResponse)
    const serviceRequestReferences =
      communication.basedOn?.filter((ref) => ref.reference?.startsWith('ServiceRequest/')) || [];

    const serviceRequestResources = serviceRequestReferences
      .map((ref) => ref.resource)
      .filter((r): r is ServiceRequest => Boolean(r && r.resourceType === 'ServiceRequest'));

    if (serviceRequestResources.length > 0 && !shouldIncludeHealthieLabOrderInTimeline(serviceRequestResources)) {
      return null;
    }

    // Extract QuestionnaireResponse reference if exists
    const questionnaireResponseReference = communication.basedOn?.find((ref) =>
      ref.reference?.startsWith('QuestionnaireResponse/')
    );

    return {
      ...baseItem,
      type: TimelineItemType.LAB_ORDER,
      serviceRequestReferences,
      questionnaireResponseReference,
      content: communication.payload?.[0]?.contentString,
    } as TimelineLabOrder;
  }

  return null;
}

function findParentCommunication(
  note: TimelineCommunicationResource,
  timelineIndex: Record<string, TimelineCommunicationResource>
): TimelineCommunicationResource | undefined {
  const basedOn = note.basedOn?.[0];

  if (!basedOn) return undefined;

  const parentRefString = getReferenceString(basedOn);

  if (!parentRefString) return undefined;

  return timelineIndex[parentRefString];
}

function mapResourceToTimelineNote(
  communication: TimelineCommunicationResource,
  parentCommunication: TimelineCommunicationResource | undefined
): TimelineNote | null {
  if (!communication.sent || !communication.id) return null;

  const linkedToRef = communication.basedOn?.[0];
  if (!linkedToRef) return null;

  const content = getPayloadByType(communication, TEXT_CONTENT_PAYLOAD_ID)?.contentString;
  const richTextContent = getPayloadByType(communication, RICH_TEXT_CONTENT_PAYLOAD_ID)?.contentString;

  return {
    reference: createReference(communication as Communication),
    resource: communication,
    type: TimelineItemType.NOTE,
    content,
    richTextContent: richTextContent ? JSON.parse(richTextContent) : undefined,
    timestamp: new Date(communication.sent),
    status: communication.status,
    sender: communication.sender?.resource,
    recipient: communication.recipient?.[0]?.resource,
    linkedTo: linkedToRef as Reference<Communication | Encounter>,
    confidentiality: getConfidentialityLevel(communication),
    hiddenPayload: communication.payload === null,
    parentConfidentiality: parentCommunication ? getConfidentialityLevel(parentCommunication) : undefined,
    isEdited: communication.payload?.some((payload) =>
      payload.extension?.some((ext) => ext.url === EDITED_COMMUNICATION_PAYLOAD_EXTENSION && ext.valueDateTime)
    ),
  };
}

export function mapCommunications(
  communications: TimelineCommunicationResource[]
): [TimelinePatientCommunication[], TimelineNote[]] {
  const timelineRegisters: TimelinePatientCommunication[] = [];
  const timelineNotes: TimelineNote[] = [];

  const timelineIndex: Record<string, TimelineCommunicationResource> = Object.fromEntries(
    communications.map((item) => [getReferenceString(item), item])
  );

  for (const communication of communications) {
    if (communication.category?.some((cat) => cat.coding?.[0]?.code === CommunicationCategory.TIMELINE_NOTE)) {
      const note = mapResourceToTimelineNote(communication, findParentCommunication(communication, timelineIndex));
      if (note) {
        timelineNotes.push(note);
      }
    } else {
      const register = mapResourceToPatientTimelineCommunication(communication);
      if (register) {
        timelineRegisters.push(register);
      }
    }
  }

  return [timelineRegisters, timelineNotes];
}

function sortByTimestampDesc<T extends { timestamp: Date }>(items: T[]): T[] {
  return items.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
}

function shouldGroupWithPrevious(sortedItems: TimelinePatientCommunication[], currentIndex: number): boolean {
  const previousItem = currentIndex > 0 ? sortedItems[currentIndex - 1] : null;
  const currentItem = sortedItems[currentIndex];

  if (!previousItem) return false;

  if (isStandaloneNote(currentItem)) {
    return false;
  }

  if (isVoiceCommunication(currentItem)) {
    return false;
  }

  // One card per intro call — never merged into a neighbouring group.
  if (isWarmHandoff(currentItem) || isWarmHandoff(previousItem)) {
    return false;
  }

  if (isCiIntroCall(currentItem) || isCiIntroCall(previousItem)) {
    return false;
  }

  if (isFaxTimelineCommunication(currentItem) || isFaxTimelineCommunication(previousItem)) {
    return false;
  }

  if (isSmsCommunication(previousItem) && isSmsCommunication(currentItem)) {
    return previousItem.patientNumber === currentItem.patientNumber;
  }

  if (isChatCommunication(previousItem) && isChatCommunication(currentItem)) {
    return getReferenceString(previousItem.thread!) === getReferenceString(currentItem.thread!);
  }

  return false;
}

/**
 * Gets timeline notes associated with a group of communications
 * @param timelineNotes - All available timeline notes
 * @param communications - Communications to find notes for
 * @returns Sorted timeline notes for the communications
 */
function getNotesForCommunications(
  communications: TimelinePatientCommunication[],
  notesIndex: Record<string, TimelineNote[]>
): TimelineNote[] {
  return communications.flatMap((communication) => notesIndex[getReferenceString(communication.reference)!] ?? []);
}

function getNotesForEncounter(
  encounter: TimelineEncounter,
  notesIndex: Record<string, TimelineNote[]>
): TimelineNote[] {
  const encounterRef = getReferenceString(encounter.reference);
  if (!encounterRef) {
    return [];
  }
  return notesIndex[encounterRef] ?? [];
}

function indexNotesByLinkedReference(timelineNotes: TimelineNote[]): Record<string, TimelineNote[]> {
  const index: Record<string, TimelineNote[]> = {};
  for (const note of timelineNotes) {
    const key = getReferenceString(note.linkedTo);
    if (!key) continue;
    if (!(key in index)) {
      index[key] = [];
    }
    index[key].push(note);
  }
  return index;
}

function addGroup(
  groups: TimelineGroup[],
  groupItems: TimelinePatientCommunication[],
  groupNotes: TimelineNote[]
): void {
  if (groupItems.length === 0) return;

  const groupType = groupItems[0].type;

  groups.push({
    type: groupType,
    id: `${groupType}-${groupItems.map((i) => getReferenceString(i.reference)).join('-')}`,
    timestamp: groupItems[0].timestamp,
    items: groupItems as any,
    notes: groupNotes,
  });
}

function addEncounterGroup(groups: TimelineGroup[], encounter: TimelineEncounter, groupNotes: TimelineNote[]): void {
  groups.push({
    type: TimelineItemType.ENCOUNTER,
    id: `encounter-${getReferenceString(encounter.reference)}`,
    timestamp: encounter.timestamp,
    items: [encounter],
    notes: groupNotes,
  });
}

// Groups one or more prescription orders into a single timeline group.
// Prescription orders are pre-batched elsewhere (see main loop in groupTimelineItems),
// typically when they share the same requester and were authored within a short time window.
function addPrescriptionOrderGroup(groups: TimelineGroup[], prescriptionOrders: TimelinePrescriptionOrder[]): void {
  if (prescriptionOrders.length === 0) return;

  const [firstOrder] = prescriptionOrders;

  groups.push({
    type: TimelineItemType.PRESCRIPTION_ORDER,
    id: `prescription-order-${prescriptionOrders
      .map((order) => getReferenceString(order.reference))
      .filter(Boolean)
      .join('-')}`,
    timestamp: firstOrder.timestamp,
    items: prescriptionOrders,
  });
}

function addAppointmentGroup(groups: TimelineGroup[], appointment: TimelineAppointment): void {
  groups.push({
    type: TimelineItemType.APPOINTMENT,
    id: `appointment-${getReferenceString(appointment.reference)}`,
    timestamp: appointment.timestamp,
    items: [appointment],
  });
}

/**
 * Maps COMPLETED clinical tasks to timeline entries ("Task completed: …").
 * Timestamp is Task.lastModified — the guarded patch protocol stamps it on
 * the completing write, so it IS the completion time. Undo removes the row
 * naturally: the entry is derived from status, not a written record.
 */
export function mapCompletedTasksToTimelineItems(tasks: FhirTask[]): TimelineTaskCompleted[] {
  const items: TimelineTaskCompleted[] = [];
  for (const task of tasks) {
    if (task.status !== 'completed' || !task.id) {
      continue;
    }
    const completedAt = task.lastModified ?? task.meta?.lastUpdated;
    if (!completedAt) {
      continue;
    }
    items.push({
      reference: createReference(task),
      type: TimelineItemType.TASK_COMPLETED,
      id: task.id,
      title: task.description ?? 'Task',
      completedBy: getClinicalTaskCompletedBy(task),
      timestamp: new Date(completedAt),
    });
  }
  return items;
}

function addTaskCompletedGroup(groups: TimelineGroup[], taskCompleted: TimelineTaskCompleted): void {
  groups.push({
    type: TimelineItemType.TASK_COMPLETED,
    id: `task-completed-${taskCompleted.id}`,
    timestamp: taskCompleted.timestamp,
    items: [taskCompleted],
    notes: [],
  });
}

function addPaymentOrderGroup(groups: TimelineGroup[], paymentOrder: TimelinePaymentOrder): void {
  groups.push({
    type: TimelineItemType.PAYMENT_ORDER,
    // Keyed on the order's own id, not getReferenceString: a payment order has
    // no FHIR reference to derive one from.
    id: `payment-order-${paymentOrder.id}`,
    timestamp: paymentOrder.timestamp,
    items: [paymentOrder],
    notes: [],
  });
}

function addBenefitEventGroup(groups: TimelineGroup[], benefitEvent: TimelineBenefitEvent): void {
  groups.push({
    type: TimelineItemType.BENEFIT_EVENT,
    id: `benefit-event-${benefitEvent.id}`,
    timestamp: benefitEvent.timestamp,
    items: [benefitEvent],
    notes: [],
  });
}

function addRefundEventGroup(groups: TimelineGroup[], refundEvent: TimelineRefundEvent): void {
  groups.push({
    type: TimelineItemType.REFUND_EVENT,
    id: `refund-event-${refundEvent.id}`,
    timestamp: refundEvent.timestamp,
    items: [refundEvent],
    notes: [],
  });
}

function addPaymentEventGroup(groups: TimelineGroup[], paymentEvent: TimelinePaymentEvent): void {
  groups.push({
    type: TimelineItemType.PAYMENT_EVENT,
    // Keyed on the charge id (or the derived paid-in-full key), not
    // getReferenceString: money has no FHIR reference here.
    id: `payment-event-${paymentEvent.id}`,
    timestamp: paymentEvent.timestamp,
    items: [paymentEvent],
    notes: [],
  });
}

function addEligibilityFormGroup(groups: TimelineGroup[], eligibilityForm: TimelineEligibilityForm): void {
  groups.push({
    type: TimelineItemType.ELIGIBILITY_FORM,
    id: `eligibility-form-${getReferenceString(eligibilityForm.reference)}`,
    timestamp: eligibilityForm.timestamp,
    items: [eligibilityForm],
    notes: [],
  });
}

interface TimelineItemWithTimestamp {
  timestamp: Date;
  item:
    | TimelinePatientCommunication
    | TimelineEncounter
    | TimelineLabOrderResults
    | TimelinePrescriptionOrder
    | TimelineAppointment
    | TimelineEligibilityForm
    | TimelinePaymentOrder
    | TimelineTaskCompleted
    | TimelinePaymentEvent
    | TimelineBenefitEvent
    | TimelineRefundEvent;
  type:
    | 'communication'
    | 'encounter'
    | 'diagnosticReport'
    | 'prescription-order'
    | 'appointment'
    | 'eligibility-form'
    | 'payment-order'
    | 'task-completed'
    | 'payment-event'
    | 'benefit-event'
    | 'refund-event';
}

/**
 * Input to {@link groupTimelineItems}.
 *
 * A named object rather than positional parameters: every field after the third
 * is an optional array of objects carrying a `timestamp`, so a mis-ordered
 * argument typechecks silently and surfaces as cards landing in the wrong place.
 */
export interface GroupTimelineItemsInput {
  timelineRegisters: TimelinePatientCommunication[];
  timelineNotes: TimelineNote[];
  encounters: TimelineEncounter[];
  diagnosticReports?: TimelineLabOrderResults[];
  prescriptionOrders?: TimelinePrescriptionOrder[];
  appointments?: TimelineAppointment[];
  eligibilityForms?: TimelineEligibilityForm[];
  /**
   * Card 01 and the append-only money events together, as
   * `mapPaymentOrderToTimelineItems` returns them. One array because the mapper
   * produces them as a set and they sort against each other.
   */
  paymentItems?: (TimelinePaymentOrder | TimelinePaymentEvent)[];
  completedTasks?: TimelineTaskCompleted[];
  /** Employer benefit events (BAR-2593), one group each. */
  benefitEvents?: TimelineBenefitEvent[];
  /** Refund request cards (BAR-3052), one group each. */
  refundEvents?: TimelineRefundEvent[];
}

export function groupTimelineItems(input: GroupTimelineItemsInput): TimelineGroup[] {
  const {
    timelineRegisters,
    timelineNotes,
    encounters,
    diagnosticReports = [],
    prescriptionOrders = [],
    appointments = [],
    eligibilityForms = [],
    paymentItems = [],
    completedTasks = [],
    benefitEvents = [],
    refundEvents = [],
  } = input;
  const sortedItems = sortByTimestampDesc([...timelineRegisters]);
  const sortedNotes = sortByTimestampDesc([...timelineNotes]);
  const sortedEncounters = sortByTimestampDesc([...encounters]);
  const sortedPrescriptionOrders = sortByTimestampDesc([...prescriptionOrders]);
  const sortedAppointments = sortByTimestampDesc([...appointments]);
  const sortedEligibilityForms = sortByTimestampDesc([...eligibilityForms]);

  const notesIndex = indexNotesByLinkedReference(sortedNotes);

  // Create a map to track the index of each communication item in sortedItems
  const communicationIndexMap = new Map(sortedItems.map((item, index) => [getReferenceString(item.reference), index]));

  // Create a unified array of all timeline items with their types for sorting
  const sortedDiagnosticReports = sortByTimestampDesc([...diagnosticReports]);
  const allItems: TimelineItemWithTimestamp[] = [
    ...sortedItems.map((item) => ({ timestamp: item.timestamp, item, type: 'communication' as const })),
    ...sortedEncounters.map((item) => ({ timestamp: item.timestamp, item, type: 'encounter' as const })),
    ...sortedDiagnosticReports.map((item) => ({ timestamp: item.timestamp, item, type: 'diagnosticReport' as const })),
    ...sortedPrescriptionOrders.map((item) => ({
      timestamp: item.timestamp,
      item,
      type: 'prescription-order' as const,
    })),
    ...sortedAppointments.map((item) => ({
      timestamp: item.timestamp,
      item,
      type: 'appointment' as const,
    })),
    ...sortByTimestampDesc([...paymentItems]).map((item) => ({
      timestamp: item.timestamp,
      item,
      type: item.type === TimelineItemType.PAYMENT_ORDER ? ('payment-order' as const) : ('payment-event' as const),
    })),
    ...sortedEligibilityForms.map((item) => ({
      timestamp: item.timestamp,
      item,
      type: 'eligibility-form' as const,
    })),
    ...sortByTimestampDesc([...completedTasks]).map((item) => ({
      timestamp: item.timestamp,
      item,
      type: 'task-completed' as const,
    })),
    ...sortByTimestampDesc([...benefitEvents]).map((item) => ({
      timestamp: item.timestamp,
      item,
      type: 'benefit-event' as const,
    })),
    ...sortByTimestampDesc([...refundEvents]).map((item) => ({
      timestamp: item.timestamp,
      item,
      type: 'refund-event' as const,
    })),
  ];

  // Sort all items together by timestamp (descending)
  const sortedAllItems = allItems.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

  const groups: TimelineGroup[] = [];
  let itemsToGroup: TimelinePatientCommunication[] = [];
  let prescriptionOrdersToGroup: TimelinePrescriptionOrder[] = [];

  for (const { item, type } of sortedAllItems) {
    if (type === 'encounter') {
      // Finish current communication group before adding encounter
      addGroup(groups, itemsToGroup, getNotesForCommunications(itemsToGroup, notesIndex));
      itemsToGroup = [];

      // Finish current prescription orders group before adding encounter
      addPrescriptionOrderGroup(groups, prescriptionOrdersToGroup);
      prescriptionOrdersToGroup = [];

      // Add encounter as its own group
      addEncounterGroup(groups, item as TimelineEncounter, getNotesForEncounter(item as TimelineEncounter, notesIndex));
    } else if (type === 'diagnosticReport') {
      // Finish current communication group before adding diagnostic report
      addGroup(groups, itemsToGroup, getNotesForCommunications(itemsToGroup, notesIndex));
      itemsToGroup = [];

      // Finish current prescription orders group before adding diagnostic report
      addPrescriptionOrderGroup(groups, prescriptionOrdersToGroup);
      prescriptionOrdersToGroup = [];

      // Add diagnostic report as its own group
      const diagnosticReport = item as TimelineLabOrderResults;
      groups.push({
        type: TimelineItemType.LAB_ORDER_RESULTS,
        id: `lab-order-results-${getReferenceString(diagnosticReport.reference)}`,
        timestamp: diagnosticReport.timestamp,
        items: [diagnosticReport],
        notes: [],
      });
    } else if (type === 'prescription-order') {
      // Finish current communication group before adding prescription orders
      addGroup(groups, itemsToGroup, getNotesForCommunications(itemsToGroup, notesIndex));
      itemsToGroup = [];

      const currentOrder = item as TimelinePrescriptionOrder;
      const lastOrder = prescriptionOrdersToGroup[prescriptionOrdersToGroup.length - 1];

      if (!lastOrder) {
        prescriptionOrdersToGroup = [currentOrder];
      } else {
        const lastRequester = lastOrder.medicationRequest.requester as Reference<Resource> | undefined;
        const currentRequester = currentOrder.medicationRequest.requester as Reference<Resource> | undefined;

        const lastRequesterRef = lastRequester ? getReferenceString(lastRequester) : undefined;
        const currentRequesterRef = currentRequester ? getReferenceString(currentRequester) : undefined;

        const timeDifferenceMs = Math.abs(currentOrder.timestamp.getTime() - lastOrder.timestamp.getTime());
        const withinFiveMinutes = timeDifferenceMs <= 5 * 60 * 1000;
        const sameRequester = lastRequesterRef && currentRequesterRef && lastRequesterRef === currentRequesterRef;

        if (withinFiveMinutes && sameRequester) {
          prescriptionOrdersToGroup.push(currentOrder);
        } else {
          addPrescriptionOrderGroup(groups, prescriptionOrdersToGroup);
          prescriptionOrdersToGroup = [currentOrder];
        }
      }
    } else if (type === 'appointment') {
      // Finish current communication group before adding appointment
      addGroup(groups, itemsToGroup, getNotesForCommunications(itemsToGroup, notesIndex));
      itemsToGroup = [];

      // Finish current prescription orders group before adding appointment
      addPrescriptionOrderGroup(groups, prescriptionOrdersToGroup);
      prescriptionOrdersToGroup = [];

      // Add appointment as its own group
      addAppointmentGroup(groups, item as TimelineAppointment);
    } else if (type === 'eligibility-form') {
      // Finish current communication group before adding the eligibility form
      addGroup(groups, itemsToGroup, getNotesForCommunications(itemsToGroup, notesIndex));
      itemsToGroup = [];

      // Finish current prescription orders group before adding the eligibility form
      addPrescriptionOrderGroup(groups, prescriptionOrdersToGroup);
      prescriptionOrdersToGroup = [];

      // One card per session — the timeline is an audit trail, never edited in place
      addEligibilityFormGroup(groups, item as TimelineEligibilityForm);
    } else if (type === 'payment-order') {
      // Finish current communication group before adding the payment order
      addGroup(groups, itemsToGroup, getNotesForCommunications(itemsToGroup, notesIndex));
      itemsToGroup = [];

      // Finish current prescription orders group before adding the payment order
      addPrescriptionOrderGroup(groups, prescriptionOrdersToGroup);
      prescriptionOrdersToGroup = [];

      addPaymentOrderGroup(groups, item as TimelinePaymentOrder);
    } else if (type === 'task-completed') {
      // Finish open groups before adding the task-completed row
      addGroup(groups, itemsToGroup, getNotesForCommunications(itemsToGroup, notesIndex));
      itemsToGroup = [];
      addPrescriptionOrderGroup(groups, prescriptionOrdersToGroup);
      prescriptionOrdersToGroup = [];

      addTaskCompletedGroup(groups, item as TimelineTaskCompleted);
    } else if (type === 'payment-event') {
      // Finish current communication group before adding the money event
      addGroup(groups, itemsToGroup, getNotesForCommunications(itemsToGroup, notesIndex));
      itemsToGroup = [];

      // Finish current prescription orders group before adding the money event
      addPrescriptionOrderGroup(groups, prescriptionOrdersToGroup);
      prescriptionOrdersToGroup = [];

      // One group per event: cards 02 and 03 are immutable, never coalesced.
      addPaymentEventGroup(groups, item as TimelinePaymentEvent);
    } else if (type === 'benefit-event') {
      addGroup(groups, itemsToGroup, getNotesForCommunications(itemsToGroup, notesIndex));
      itemsToGroup = [];
      addPrescriptionOrderGroup(groups, prescriptionOrdersToGroup);
      prescriptionOrdersToGroup = [];

      // One group per event: append-only, never coalesced.
      addBenefitEventGroup(groups, item as TimelineBenefitEvent);
    } else if (type === 'refund-event') {
      addGroup(groups, itemsToGroup, getNotesForCommunications(itemsToGroup, notesIndex));
      itemsToGroup = [];
      addPrescriptionOrderGroup(groups, prescriptionOrdersToGroup);
      prescriptionOrdersToGroup = [];

      addRefundEventGroup(groups, item as TimelineRefundEvent);
    } else {
      // Handle communication items
      const communication = item as TimelinePatientCommunication;
      const refString = getReferenceString(communication.reference);
      const currentIndex = communicationIndexMap.get(refString) ?? -1;

      if (currentIndex > 0 && shouldGroupWithPrevious(sortedItems, currentIndex)) {
        itemsToGroup.push(communication);
        continue;
      }

      // Finish current prescription orders group before starting a new communication group
      addPrescriptionOrderGroup(groups, prescriptionOrdersToGroup);
      prescriptionOrdersToGroup = [];

      addGroup(groups, itemsToGroup, getNotesForCommunications(itemsToGroup, notesIndex));
      itemsToGroup = [communication];
    }
  }

  // Add any remaining communication items
  addGroup(groups, itemsToGroup, getNotesForCommunications(itemsToGroup, notesIndex));
  // Add any remaining prescription orders
  addPrescriptionOrderGroup(groups, prescriptionOrdersToGroup);

  return groups;
}

export function mapEncountersToTimelineItems(encounters: TimelineEncounterResource[]): TimelineEncounter[] {
  return encounters
    .filter((encounter) => encounter.period?.start && encounter.id)
    .map((encounter) => ({
      type: TimelineItemType.ENCOUNTER,
      encounter: encounter as unknown as Encounter,
      reference: createReference(encounter as unknown as Encounter) as Reference<Encounter>,
      resource: encounter as unknown as TimelineCommunicationResource,
      timestamp: new Date(encounter.period!.start!),
      status: undefined,
      hiddenPayload: false,
      sender: undefined,
      recipient: undefined,
      content: undefined,
      confidentiality: undefined,
      priorityStatus: undefined,
      isAssignable: undefined,
    }));
}

export function mapDiagnosticReportsToTimelineItems(diagnosticReports: any[]): any[] {
  return diagnosticReports
    .filter((report) => report.issued && report.id)
    .map((report) => ({
      type: TimelineItemType.LAB_ORDER_RESULTS,
      diagnosticReport: report,
      reference: createReference(report),
      resource: report as unknown as TimelineCommunicationResource,
      timestamp: new Date(report.issued),
      status: report.status,
      hiddenPayload: false,
      sender: undefined,
      recipient: undefined,
      content: undefined,
      confidentiality: getConfidentialityLevel(report),
      priorityStatus: undefined,
      isAssignable: false,
      serviceRequestReference: report.basedOn?.[0],
    }));
}

/**
 * AuthoredOn usually is a date time string without a timezone, it is created in DoseSpot so we don't have any control over it.
 * This function will try to parse the string as a date time string with a UTC timezone and if it fails,
 * it will return the date time string without a timezone.
 */
function formatTimestamp(medicationRequest: MedicationRequest): Date {
  const authoredOn = medicationRequest.authoredOn;

  if (authoredOn) {
    if (authoredOn.includes('Z')) {
      return new Date(authoredOn);
    }

    try {
      return new Date(`${authoredOn}Z`);
    } catch {
      return new Date(authoredOn);
    }
  }

  // Fallback to current date only to avoid type errors, but will not be displayed in the timeline
  return new Date(dayjs().toISOString());
}

function formatDosageInstruction(medicationRequest: MedicationRequest): string | undefined {
  const dosage = medicationRequest.dosageInstruction?.[0];
  if (!dosage) return undefined;

  const parts: string[] = [];

  // Add dose quantity if available
  const doseQuantity = dosage.doseAndRate?.[0]?.doseQuantity;
  if (doseQuantity?.value !== undefined) {
    parts.push(`${doseQuantity.value}${doseQuantity.unit ? ` ${doseQuantity.unit}` : ''}`);
  }

  // Add timing if available
  if (dosage.timing?.code?.text) {
    parts.push(dosage.timing.code.text);
  }

  // Fall back to text if available and no structured data
  if (parts.length === 0 && dosage.text) {
    return dosage.text;
  }

  return parts.length > 0 ? parts.join(', ') : undefined;
}

function formatQuantity(medicationRequest: MedicationRequest): string | undefined {
  const quantity = medicationRequest.dispenseRequest?.quantity;
  if (!quantity?.value) return undefined;

  const parts = [`${quantity.value}${quantity.unit ? ` ${quantity.unit}` : ''}`];

  const refills = medicationRequest.dispenseRequest?.numberOfRepeatsAllowed;
  if (refills !== undefined && refills > 0) {
    parts.push(`(${refills} refill${refills > 1 ? 's' : ''})`);
  }

  return parts.join(' ');
}

export function mapPrescriptionOrdersToTimelineItems(
  medicationRequests: MedicationRequest[]
): TimelinePrescriptionOrder[] {
  return medicationRequests
    .filter((medicationRequest) => !!medicationRequest.id)
    .map((medicationRequest) => ({
      type: TimelineItemType.PRESCRIPTION_ORDER,
      medicationRequest,
      reference: createReference(medicationRequest) as Reference<MedicationRequest>,
      timestamp: formatTimestamp(medicationRequest),
      requester: (medicationRequest.requester as { resource?: ProfileFragment })?.resource,
      medicationDisplay:
        formatCodeableConcept(medicationRequest.medicationCodeableConcept) ??
        formatReferenceString(medicationRequest.medicationReference),
      dosageInstruction: formatDosageInstruction(medicationRequest),
      quantity: formatQuantity(medicationRequest),
    }));
}

/**
 * Eligibility-form QRs -> timeline items. `authored` is when the lead first hit
 * screen 9, which is where the card belongs on the spine; `meta.lastUpdated`
 * moves as answers accumulate and drives the drop-off derivation instead.
 */
export function mapEligibilityFormsToTimelineItems(
  questionnaireResponses: (TimelineEligibilityFormResource & Resource)[]
): TimelineEligibilityForm[] {
  return questionnaireResponses.reduce((timelineItems, questionnaireResponse) => {
    const authored = questionnaireResponse.authored ?? questionnaireResponse.meta?.lastUpdated;
    if (questionnaireResponse.id && authored) {
      timelineItems.push({
        type: TimelineItemType.ELIGIBILITY_FORM,
        questionnaireResponse,
        reference: createReference(questionnaireResponse),
        timestamp: new Date(authored),
        sessionId: questionnaireResponse.identifier?.[0]?.value,
      });
    }
    return timelineItems;
  }, [] as TimelineEligibilityForm[]);
}

export function mapAppointmentsToTimelineItems(
  appointments: (AppointmentFragment & Resource)[]
): TimelineAppointment[] {
  return appointments.reduce((timelineItems, appointment) => {
    if (!!appointment.id && !!appointment.start) {
      appointment.extension = clearBlankExtensionValues(appointment.extension ?? []);
      const contactType = extractContactTypeFromAppointment(appointment);
      const virtualServiceUrl = extractVirtualServiceUrlFromAppointment(appointment);
      const virtualServicePhone = extractVirtualServicePhoneNumberFromAppointment(appointment);
      const location = extractLocationFromAppointment(appointment) as LocationFragment[];
      const virtualServiceAdminUrl = extractVirtualServiceAdminUrlFromAppointment(appointment);

      timelineItems.push({
        type: TimelineItemType.APPOINTMENT,
        appointment,
        reference: createReference(appointment),
        timestamp: new Date(appointment.start!),
        contactType,
        virtualServiceUrl,
        virtualServiceAdminUrl,
        virtualServicePhone,
        location: location?.[0],
      });
    }

    return timelineItems;
  }, [] as TimelineAppointment[]);
}
